// ----------------------------------------------------------------------------
// Main index file, export function VtkInitModel
// ----------------------------------------------------------------------------

import '@kitware/vtk.js/favicon';

// css
import './scss/vxvtk.scss';

// Load the rendering pieces we want to use (for both WebGL and WebGPU)
import '@kitware/vtk.js/Rendering/Profiles/Geometry';
import vtkActor from '@kitware/vtk.js/Rendering/Core/Actor';
//import vtkFullScreenRenderWindow from '@kitware/vtk.js/Rendering/Misc/FullScreenRenderWindow';
import vtkOpenGLRenderWindow from '@kitware/vtk.js/Rendering/OpenGL/RenderWindow';
import vtkRenderWindow from '@kitware/vtk.js/Rendering/Core/RenderWindow';
import vtkRenderWindowInteractor from '@kitware/vtk.js/Rendering/Core/RenderWindowInteractor';
import vtkInteractorStyleTrackballCamera from '@kitware/vtk.js/Interaction/Style/InteractorStyleTrackballCamera';
import vtkRenderer from '@kitware/vtk.js/Rendering/Core/Renderer';

import HttpDataAccessHelper from '@kitware/vtk.js/IO/Core/DataAccessHelper/HttpDataAccessHelper';
import DataAccessHelper from '@kitware/vtk.js/IO/Core/DataAccessHelper';
import vtkHttpSceneLoader from '@kitware/vtk.js/IO/Core/HttpSceneLoader';
import vtkMapper from '@kitware/vtk.js/Rendering/Core/Mapper';
//import vtkProperty from '@kitware/vtk.js/Rendering/Core/Property';
// Force DataAccessHelper to have access to various data source
import '@kitware/vtk.js/IO/Core/DataAccessHelper/JSZipDataAccessHelper';

// Vx library
import vxVtkProgress from "./lib/vxVtkProgress.js"
import vxVtkOrientationWidget from "./lib/vxVtkOrientationWidget.js"
import vxVtkCutter from "./lib/vxVtkCutter.js"
import vxVtkSceneSwitcher from "./lib/vxVtkSceneSwitcher.js"

// global var
const defaultConfig = {
    modelName: 'scene',
    enableCut: false
}
class vtkModel {
    static renderWindow ;
    static renderer;
    static modelMapper;
    static modelActor;
    static scene;
    static sceneConfig;
    static widgetManager;
    static container;
    static openGLRenderWindow;
}


// Handle window resize
function resize() {
  const dims = vtkModel.container.getBoundingClientRect();
  const windowWidth = Math.floor(dims.width);
  const windowHeight = Math.floor(dims.height);
  vtkModel.openGLRenderWindow.setSize(windowWidth, windowHeight);
//   textCanvas.setAttribute('width', windowWidth);
//   textCanvas.setAttribute('height', windowHeight);
//   if (debugHandler) {
//     debugHandler.resize(windowWidth, windowHeight);
//   }
  vtkModel.renderWindow.render();
}

//call counter - one per scene
let numcall = 0;
// init model when scene is finaly loaded
function modelLoadedCallback(sceneImporter) {
    //let numscene = sceneImporter.getScene().length;
    numcall++;
    if (numcall > 1) {
        return;
    }
    vxVtkProgress.hide();

    // ----------------------------------------------------------------------------
    // Import scene
    // ----------------------------------------------------------------------------
    vtkModel.scene = sceneImporter.getScene();

    //console.log(modelMapper.getBounds());
    vtkModel.renderer.resetCamera();




    // interactor - mouse gesture
    const interactor = vtkRenderWindowInteractor.newInstance();
    interactor.setView(vtkModel.openGLRenderWindow);
    interactor.initialize();
    interactor.bindEvents(vtkModel.container);

    interactor.setInteractorStyle(vtkInteractorStyleTrackballCamera.newInstance());


    // -----------------------------------------------------------
    // Init cutter if enabled
    // -----------------------------------------------------------
    if (vtkModel.sceneConfig.enableCut) {

        // initial hide scene
        for (let i = 0; i < vtkModel.scene.length; i++) {
            vtkModel.scene[i].actor.setVisibility(false);
        }

        vtkModel.modelMapper = vtkMapper.newInstance();
        vtkModel.modelMapper.setScalarVisibility(false);
        vtkModel.modelActor = vtkActor.newInstance();
        vtkModel.modelActor.setMapper(vtkModel.modelMapper);
        vtkModel.renderer.addActor(vtkModel.modelActor);
        vtkModel.modelMapper.setInputConnection(vtkModel.scene[0].source.getOutputPort());

        // set transparent
        vtkModel.scene[0].actor.setVisibility(false);

        vxVtkCutter.init(vtkModel);
        const modelProperty = vtkModel.modelActor.getProperty();
        //modelProperty.setRepresentation(vtkProperty.Representation.WIREFRAME);
        //modelProperty.setLighting(false);
        modelProperty.setOpacity(0.3);
        // set source/input connection to cutter
        vxVtkCutter.setSource(vtkModel.scene[0].source);
        //update cut plane 
        vxVtkCutter.updatePlaneFunction();

    }
    else {
        //for (let i = 0; i < vtkModel.scene.length; i++) {
        //    vtkModel.scene[i].actor.setVisibility(true);
        //}
            // initial hide scene
        vxVtkSceneSwitcher.init(vtkModel);
    }
    // vtkModel.renderWindow.render();

    // ----------------------------------------------------------------------------
    // Setup an interactor to handle mouse events
    // ----------------------------------------------------------------------------

    // ----------------------------------------------------------------------------
    // Orientation widget
    // ----------------------------------------------------------------------------
    vtkModel.widgetManager = vxVtkOrientationWidget(vtkModel);
    vtkModel.widgetManager.enablePicking();


    window.addEventListener('resize', resize);

    resize();
   

}

// exported function for starting up the model
export function VtkInitModel(config) {

    // ----------------------------------------------------------------------------
    // Merge config with default
    // ----------------------------------------------------------------------------
    vtkModel.sceneConfig=Object.assign({} , defaultConfig, config);

    // ----------------------------------------------------------------------------
    // Standard rendering code setup
    // ----------------------------------------------------------------------------
    vtkModel.renderWindow  = vtkRenderWindow.newInstance();
    vtkModel.renderer = vtkRenderer.newInstance({ background: [0, 0, 0]});
    vtkModel.renderWindow.addRenderer(vtkModel.renderer);



    // ----------------------------------------------------------------------------
    // Use OpenGL as the backend to view the all this
    // ----------------------------------------------------------------------------
    vtkModel.container = document.querySelector(config.cointainer);
    vtkModel.openGLRenderWindow = vtkOpenGLRenderWindow.newInstance();
    vtkModel.renderWindow.addView(vtkModel.openGLRenderWindow);

    const dims = vtkModel.container.getBoundingClientRect();
    const windowWidth = Math.floor(dims.width);
    const windowHeight = Math.floor(dims.height);
    vtkModel.openGLRenderWindow.setSize(windowWidth, windowHeight);

    vtkModel.openGLRenderWindow.setContainer(vtkModel.container);



    // ----------------------------------------------------------------------------
    // Progress loading
    // ----------------------------------------------------------------------------
    vxVtkProgress.init(vtkModel);
    const progressCallback = (progressEvent) => { vxVtkProgress.progressCallback(progressEvent); };

    // ----------------------------------------------------------------------------
    // Load scene zip, can we manage multiple formats?
    // ----------------------------------------------------------------------------
    HttpDataAccessHelper.fetchBinary(
        'vtkdata/' + vtkModel.sceneConfig.modelName,
        { progressCallback }
    ).then((zipContent) => {
        const dataAccessHelper = DataAccessHelper.get('zip', {
            zipContent,
            callback: (zip) => {
                
                let renderer=vtkModel.renderer
                const sceneImporter = vtkHttpSceneLoader.newInstance({
                    renderer,
                    dataAccessHelper,
                });
                sceneImporter.setUrl('index.json');
                sceneImporter.onReady(() => {
                    modelLoadedCallback(sceneImporter);
                });
            },
        });
    });
};